import { FC, Fragment } from 'react';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { AddableQuestionType, AddQuestionButton } from './add-question-button';
import { QuestionCard } from './question-card';
import type {
  CutsheetCatalog,
  CutsheetQuestionnaire,
  Question,
} from '../../types';

export interface QuestionnairePanelProps {
  questionnaire: CutsheetQuestionnaire | null;
  questionnaireLoading: boolean;
  capabilities: CutsheetCatalog;
  /** Type of capability currently being dragged (forwarded to QuestionCard). */
  draggingType?: 'cut' | 'trim' | 'extra' | 'specification' | null;
  onAddQuestion: (type: AddableQuestionType, insertAtIndex: number) => void;
  onEditQuestion: (question: Question) => void;
  onDeleteQuestion: (questionId: string) => void;
  onAddItem: (questionId: string, itemId: string) => void;
  onAddItems: (questionId: string, itemIds: string[]) => void;
  onRemoveItem: (questionId: string, itemId: string) => void;
  onEditItem: (itemId: string, itemType: 'cut' | 'trim' | 'extra') => void;
  onAddCutToOption: (questionId: string, optionId: string) => void;
  onRemoveCutFromOption: (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => void;
  onReorderQuestions: (fromIndex: number, toIndex: number) => void;
  onReorderItems: (questionId: string, newOrderIds: string[]) => void;
}

/**
 * Right-hand questionnaire builder panel.  Renders the ordered list of
 * questions together with "add question" buttons between each question.
 * @param root0
 * @param root0.questionnaire
 * @param root0.questionnaireLoading
 * @param root0.capabilities
 * @param root0.draggingType
 * @param root0.onAddQuestion
 * @param root0.onEditQuestion
 * @param root0.onDeleteQuestion
 * @param root0.onAddItem
 * @param root0.onAddItems
 * @param root0.onRemoveItem
 * @param root0.onEditItem
 * @param root0.onAddCutToOption
 * @param root0.onRemoveCutFromOption
 * @param root0.onReorderQuestions
 * @param root0.onReorderItems
 */
export const QuestionnairePanel: FC<QuestionnairePanelProps> = ({
  questionnaire,
  questionnaireLoading,
  capabilities,
  draggingType = null,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onAddItem,
  onAddItems,
  onRemoveItem,
  onEditItem,
  onAddCutToOption,
  onRemoveCutFromOption,
  onReorderQuestions,
  onReorderItems,
}) => (
  <Paper sx={{ p: 3, height: 'calc(100vh - 108px)', overflow: 'auto' }}>
    {questionnaireLoading || !questionnaire ? (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    ) : (
      <Stack spacing={2}>
        <AddQuestionButton insertAtIndex={0} onSelectType={onAddQuestion} />

        {questionnaire.questions
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((question, index, sortedQuestions) => (
            <Fragment key={question.title + question.id}>
              <QuestionCard
                question={question}
                catalog={capabilities}
                onEdit={onEditQuestion}
                onDelete={onDeleteQuestion}
                onAddItem={onAddItem}
                onAddItems={onAddItems}
                onRemoveItem={onRemoveItem}
                onEditItem={onEditItem}
                onAddCutToOption={onAddCutToOption}
                onRemoveCutFromOption={onRemoveCutFromOption}
                onMoveUp={
                  index > 0
                    ? () => onReorderQuestions(index, index - 1)
                    : undefined
                }
                onMoveDown={
                  index < sortedQuestions.length - 1
                    ? () => onReorderQuestions(index, index + 1)
                    : undefined
                }
                draggingType={draggingType}
                onReorderItems={onReorderItems}
              />
              <AddQuestionButton
                insertAtIndex={index + 1}
                onSelectType={onAddQuestion}
              />
            </Fragment>
          ))}
      </Stack>
    )}
  </Paper>
);
